import React, {useCallback} from 'react';
import styled from 'styled-components';
import {Typography, VerticalSpacer, RemoveIcon} from '../../elements';
import {PersonalInfo, SocialProps} from './Person';
import Grid from '@material-ui/core/Grid';
import {UeLink} from '../../wraps';
import {
    FacebookIcon,
    TwitterIcon,
    LinkedInIcon,
    InstagramIcon
} from '../../elements/icons/socials/';
import {useCurrentAccentColor} from 'epic-ue-theme';

const BioModalWrapper = styled.div`
    width: 780px;
    ${(p) => p.theme.breakpoints.down('sm')} {
        width: 100vw;
    }
    margin: auto;
    position: fixed;
    padding: ${(p) => p.theme.spacing(6)}px ${(p) => p.theme.spacing(9)}px;
    ${(p) => p.theme.breakpoints.down('sm')} {
        padding: ${(p) => p.theme.spacing(6)}px ${(p) => p.theme.spacing(2)}px;
    }
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: 999;
    background: ${(p) =>
        p.theme.ifLight(p, p.theme.palette.neutral.chalk, p.theme.palette.neutral.concrete)};
    opacity: 0;
    visibility: hidden;
    height: 0;
    .paragraph {
        color: ${(p) => p.theme.palette.neutral.stone};
    }
    ul {
        list-style: none;
        margin: 0;
        padding: 0;
        ${(p) => p.theme.breakpoints.up('md')} {
            text-align: right;
        }
        li {
            ${(p) => p.theme.breakpoints.down('sm')} {
                display: inline-block;
                padding-right: ${(p) => p.theme.spacing(3)}px;
                &:last-child {
                    padding-right: 0;
                }
            }
        }
    }
    &.modal-open {
        opacity: 1;
        visibility: visible;
        height: fit-content;
        ${(p) => p.theme.breakpoints.down('sm')} {
            margin-top: ${(p) => p.theme.spacing(4)}px;
            height: 100vh;
        }
    }
    .bio-content {
        height: 505px;

        ${(p) => p.theme.breakpoints.down('sm')} {
            height: 78vh;
        }

        overflow-y: scroll;

        padding-right: 25px;

        /*style scrollbar*/
        ::-webkit-scrollbar {
            max-width: 1px;
        }

        ::-webkit-scrollbar-thumb {
            background-color: darkgrey;
            outline: 1px solid slategrey;
        }
    }
    .social-icons ul {
        margin-right: 10px;
    }
    button {
    }
`;

const CloseButton = styled.button<{name: string}>`
    color: ${(p) => p.theme.palette.text.primary};
    background: none;
    position: absolute;
    right: 45px;
    border: 0;
    outline: none;
    font-size: 1.2em;
    padding: 0;
    margin: 0;
    cursor: pointer;
    ${(p) => p.theme.breakpoints.down('sm')} {
        font-size: 1.5em;
    }

    svg {
        transition: color 0.25s ${(p) => p.theme.easing}, opacity 0.25s ${(p) => p.theme.easing},
            transform 0.25s ${(p) => p.theme.easing};
    }

    &:focus,
    &:hover {
        svg {
            color: ${(p) => p.theme.palette.accent[p.name]};
        }
    }

    &:active {
        svg {
            opacity: 0.5;
            transform: scale(0.8);
        }
    }
`;

export const SocialIcon = ({platform}: SocialProps): JSX.Element => {
    switch (platform) {
        case 'facebook':
            return <FacebookIcon />;
        case 'twitter':
            return <TwitterIcon />;
        case 'instagram':
            return <InstagramIcon />;
        case 'linkedin':
            return <LinkedInIcon />;
        default:
            return <span />;
    }
};

type ClickCloseHandler = (index: null) => void;
export interface BioModalProps {
    classes?: string;
    isOpen: boolean;
    name?: string;
    caption?: string;
    shortInfo?: string;
    bio?: string;
    socials?: SocialProps[];
    onClick: ClickCloseHandler;
}

export const BioModal = ({
    classes,
    isOpen,
    name,
    caption,
    shortInfo,
    bio,
    socials,
    onClick
}: BioModalProps): JSX.Element => {
    const bioClose = useCallback(
        (e) => {
            if (onClick && typeof onClick === 'function') {
                onClick(null);
            }
        },
        [onClick]
    );

    const accentColor = useCurrentAccentColor();
    const colorName = accentColor.name || 'sky';

    return (
        <BioModalWrapper className={`${classes} ${isOpen ? 'modal-open' : ''}`}>
            <div className="bio-content">
                <CloseButton onClick={bioClose} name={colorName}>
                    <RemoveIcon />
                </CloseButton>
                <PersonalInfo
                    name={name}
                    caption={caption}
                    shortInfo={shortInfo}
                    isInModal={true}
                />
                <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
                    <Grid item xs={11} md={8}>
                        {bio && (
                            <>
                                <VerticalSpacer variant="custom" spacing={3} />
                                <Typography variant="p">{bio}</Typography>
                            </>
                        )}
                    </Grid>
                    <Grid item xs={12} md={2}></Grid>
                    <Grid className="social-icons" item xs={12} md={2}>
                        <VerticalSpacer variant="heading" />
                        <ul>
                            {socials &&
                                socials.map((social, i): JSX.Element => {
                                    const {url, platform}: SocialProps = social;
                                    if (url && platform) {
                                        return (
                                            <li key={`f-biomodal-social-${i}`}>
                                                <UeLink href={url}>
                                                    <SocialIcon url={url} platform={platform} />
                                                </UeLink>
                                                <VerticalSpacer variant="custom" spacing={3} />
                                            </li>
                                        );
                                    } else {
                                        return <li key={`f-biomodal-social-${i}`} />;
                                    }
                                })}
                        </ul>
                    </Grid>
                </Grid>
            </div>
        </BioModalWrapper>
    );
};
